import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

export interface CreateCourseDto {
  name: string;
  description?: string;
  tags: string[];
  isActive: boolean;
}

export interface UpdateCourseDto {
  name?: string;
  description?: string;
  tags?: string[];
  isActive?: boolean;
  displayOrder?: number;
}

export interface CourseFilters {
  search?: string;
  tags?: string[];
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: "name" | "displayOrder" | "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
}

export interface CourseData {
  id: string;
  name: string;
  coverImage?: string;
  description?: string;
  tags: string[];
  isActive: boolean;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  topics?: TopicData[];
}

export interface TopicData {
  id: string;
  title: string;
  displayOrder: number;
  learningMaterials?: LearningMaterialData[];
  enrollments?: EnrollmentData[];
}

export interface LearningMaterialData {
  id: string;
  title: string;
  displayOrder: number;
  fileUpload?: FileUploadData;
  test?: TestData;
}

export interface FileUploadData {
  id: string;
  filename: string;
  fileType: string;
  status: string;
  duration?: number;
  thumbnailUrl?: string;
}

export interface TestData {
  id: string;
  title: string;
  timeLimit?: number;
  type: string;
  difficulty: string;
  passingScore: number;
}

export interface EnrollmentData {
  id: string;
}

export interface CourseResponse {
  success: boolean;
  message: string;
  data: CourseData;
  timestamp: string;
}

export interface CourseListResponse {
  success: boolean;
  message: string;
  data: CourseData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  timestamp: string;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const courseApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/courses`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(courseApiClient);

courseApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message: errorData?.message || error.message || "An error occurred",
      statusCode: error.response?.status || 0,
      error: errorData?.error,
    };
    return Promise.reject(apiError);
  }
);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Build query string from parameters object
 */
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        searchParams.append(key, value.join(","));
      } else {
        searchParams.append(key, String(value));
      }
    }
  }

  return searchParams.toString();
}

// ============================================================================
// COURSE API FUNCTIONS
// ============================================================================

/**
 * Create a new course
 */
export async function createCourse(
  courseData: CreateCourseDto
): Promise<CourseResponse> {
  const response = await courseApiClient.post<CourseResponse>("", courseData);
  return response.data;
}

/**
 * Get a list of courses with optional filtering and pagination
 */
export async function getCourses(
  filters?: CourseFilters
): Promise<CourseListResponse> {
  const queryString = filters
    ? buildQueryString(filters as Record<string, unknown>)
    : "";
  const url = queryString ? `?${queryString}` : "";

  const response = await courseApiClient.get<CourseListResponse>(url);
  return response.data;
}

export async function getPublishedCourses(
  filters?: CourseFilters
): Promise<CourseListResponse> {
  const updatedFilters = { ...filters, isActive: true };
  const queryString = updatedFilters
    ? buildQueryString(updatedFilters as Record<string, unknown>)
    : "";
  const url = queryString ? `?${queryString}` : "";

  const response = await courseApiClient.get<CourseListResponse>(url);
  return response.data;
}

/**
 * Get a single course by ID
 */
export async function getCourse(courseId: string): Promise<CourseResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await courseApiClient.get<CourseResponse>(
    `/${encodeURIComponent(courseId)}`
  );
  return response.data;
}

/**
 * Course-builder aggregate: everything the edit page needs in one request.
 *
 * Replaces `getCourse` + one `getTest` per learning material + `searchTests` —
 * a fan-out that put up to ~80 concurrent requests on the origin and mostly
 * came back 5xx. `include` is an allow-list: omit it for the lean tree
 * (topics, materials, test stubs), pass tokens to expand the question trees.
 */
export type CourseBuilderInclude = "tests" | "assessment";

export async function getCourseBuilderData(
  courseId: string,
  include: CourseBuilderInclude[] = ["tests", "assessment"]
): Promise<CourseResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const query = include.length ? `?include=${include.join(",")}` : "";

  const response = await courseApiClient.get<CourseResponse>(
    `/${encodeURIComponent(courseId)}/builder${query}`
  );
  return response.data;
}

/**
 * Update an existing course
 */
export async function updateCourse(
  courseId: string,
  updateData: UpdateCourseDto
): Promise<CourseResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  if (!updateData || Object.keys(updateData).length === 0) {
    throw {
      message: "Update data is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await courseApiClient.put<CourseResponse>(
    `/${encodeURIComponent(courseId)}`,
    updateData
  );
  return response.data;
}

/**
 * Delete a course permanently
 */
export async function deleteCourse(courseId: string): Promise<DeleteResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await courseApiClient.delete<DeleteResponse>(
    `/${encodeURIComponent(courseId)}`
  );
  return response.data;
}

/**
 * Deactivate a course (soft delete)
 */
export async function deactivateCourse(
  courseId: string
): Promise<CourseResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await courseApiClient.put<CourseResponse>(
    `/${encodeURIComponent(courseId)}/deactivate`
  );
  return response.data;
}

/**
 * Activate a course (soft delete)
 */
export async function activateCourse(
  courseId: string
): Promise<CourseResponse> {
  if (!courseId?.trim()) {
    throw {
      message: "Course ID is required",
      statusCode: 400,
    } as ApiError;
  }

  const response = await courseApiClient.put<CourseResponse>(
    `/${encodeURIComponent(courseId)}/activate`
  );
  return response.data;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get courses by specific tags
 */
export async function getCoursesByTags(
  tags: string[],
  options?: Omit<CourseFilters, "tags">
): Promise<CourseListResponse> {
  return getCourses({
    ...options,
    tags,
  });
}

/**
 * Search courses by name or description
 */
export async function searchCourses(
  searchTerm: string,
  options?: Omit<CourseFilters, "search">
): Promise<CourseListResponse> {
  return getCourses({
    ...options,
    search: searchTerm,
  });
}
